import React from 'react';
import { Heart, Sparkles, Users, Calendar, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';

const BridalSection = () => {
  const bridalServices = [
    {
      title: "Bridal Relaxation Package",
      description: "Help the bride-to-be unwind before the big day with a full body massage tailored to release tension and promote calm."
    },
    {
      title: "Bridesmaids Pamper Party",
      description: "Treat your bridal party to a group massage experience. Perfect for hen parties or the day before the wedding."
    },
    {
      title: "Pre-Wedding Couples Massage",
      description: "A romantic relaxation session for the couple to share together before their special day."
    },
    {
      title: "Wedding Day On-Site Services",
      description: "We come to your venue with our mobile setup to help the bridal party relax on the morning of the wedding."
    }
  ];

  const features = [
    "Customised packages for any group size",
    "Mobile service - we come to you",
    "Professional, experienced therapists",
    "Flexible scheduling around your wedding plans"
  ];

  return (
    <section id="bridal" className="py-20 sm:py-24 bg-gradient-to-br from-rose-50 via-white to-warm-50 relative overflow-hidden" data-testid="bridal-section">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-rose-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-48 h-48 bg-sage-200/30 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-rose-100 px-4 py-2 rounded-full text-sm font-medium text-rose-700 mb-4">
            <Heart size={16} className="fill-rose-500 text-rose-500" />
            <span>Bridal Services</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-4">
            Bridal & Wedding
            <span className="block text-rose-500">Massage Packages</span>
          </h2>
          
          <p className="text-stone-600 text-base sm:text-lg max-w-2xl mx-auto">
            Make your special day even more memorable. We offer bespoke massage services 
            for brides, bridal parties, and couples preparing for their wedding.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-12">
          {bridalServices.map((service) => (
            <div 
              key={service.title}
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-rose-100 hover:shadow-lg hover:border-rose-200 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-rose-400 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 text-base sm:text-lg mb-2">{service.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{service.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Features & CTA */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-lg border border-rose-100">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Features List */}
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-stone-800 mb-6">
                Why Choose Us for Your Wedding?
              </h3>
              <div className="space-y-4">
                {features.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={14} className="text-rose-500" />
                    </div>
                    <span className="text-stone-700 text-sm sm:text-base">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-6 sm:p-8 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar size={28} className="text-white" />
              </div>
              <h4 className="text-xl sm:text-2xl font-semibold mb-3">Get Your Custom Quote</h4>
              <p className="text-rose-100 text-sm sm:text-base mb-6">
                Tell us your ideal service and party size, and we'll create a personalised package just for you.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => window.open(`https://wa.me/${businessInfo.whatsapp}?text=Hi! I'm interested in bridal massage packages for my wedding.`, '_blank')}
                  className="bg-white text-rose-600 hover:bg-rose-50 px-6 py-3 rounded-full transition-all duration-300 group"
                  data-testid="bridal-whatsapp-btn"
                >
                  <Users size={18} className="mr-2" />
                  WhatsApp Us
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => window.open(`mailto:${businessInfo.email}?subject=Bridal Package Enquiry`, '_blank')}
                  variant="outline"
                  className="border-2 border-white/50 text-white hover:bg-white/10 px-6 py-3 rounded-full"
                  data-testid="bridal-email-btn"
                >
                  Email Enquiry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BridalSection;
